import { useFragment } from "@/generated";
import {
  EditSlideTemplateDocument,
  SlideFieldsFragmentDoc,
  type SlideTemplateEnum,
} from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for changing an existing slide's template and name.
 *
 * The server resets `slideData` only when the template actually changes, and derives the legacy
 * `type`/`knowledgeArea` from the template. Apollo's normalized cache updates every query that
 * references the slide when the mutation returns.
 *
 * @example
 * const { editSlideTemplate, loading } = useEditSlideTemplate();
 * const updated = await editSlideTemplate("slide123", "New Name", SlideTemplateEnum.Vocabulary);
 */
export function useEditSlideTemplate() {
  const [editSlideTemplateMutation, { loading }] = useMutation(EditSlideTemplateDocument);

  const editSlideTemplate = async (slideId: string, name: string, template: SlideTemplateEnum) => {
    return withToast(
      async () => {
        const result = await editSlideTemplateMutation({
          variables: { name, template, slideId },
        });

        const maskedSlide = result.data?.editSlideTemplate;
        return maskedSlide ? useFragment(SlideFieldsFragmentDoc, maskedSlide) : undefined;
      },
      {
        loading: "Updating slide template...",
        success: `Successfully edited slide "${name}"`,
        error: "Failed to edit slide. Please try again.",
      },
      "editSlideTemplate"
    );
  };

  return { editSlideTemplate, loading };
}
